import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Database, FileText, Users, Cookie } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../mockData';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            Torna alla Home
          </button>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-gray-400 mt-2">Ultimo aggiornamento: Dicembre 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduzione */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <Shield className="text-green-600" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Introduzione</h2>
              <p className="text-gray-700 leading-relaxed">
                La presente Privacy Policy descrive le modalità di trattamento dei dati personali degli utenti 
                che consultano il sito web di {companyInfo.name}. Ci impegniamo a proteggere la tua privacy 
                e a garantire la sicurezza dei tuoi dati personali in conformità al Regolamento (UE) 2016/679 (GDPR).
              </p>
            </div>
          </div>
        </div>

        {/* Titolare del Trattamento */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <Users className="text-green-600" size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Titolare del Trattamento</h2>
              <div className="space-y-3 text-gray-700">
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">Ragione Sociale:</span>
                  <span>{companyInfo.name}</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">Sede Legale:</span>
                  <span>{companyInfo.address}</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">Partita IVA:</span>
                  <span>{companyInfo.piva}</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">Email:</span>
                  <a href={`mailto:${companyInfo.email}`} className="text-green-600 hover:text-green-700">
                    {companyInfo.email}
                  </a>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-900">Telefono:</span>
                  <span>{companyInfo.phone} / {companyInfo.office}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dati Raccolti */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <Database className="text-green-600" size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Dati Raccolti</h2>
              <p className="text-gray-700 mb-6">
                Durante la navigazione sul nostro sito e l'utilizzo dei servizi offerti, 
                potrebbero essere raccolti i seguenti dati personali:
              </p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Dati Forniti Volontariamente</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Nome e Cognome:</strong> raccolto tramite il form di richiesta preventivo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Email:</strong> necessaria per inviarti comunicazioni e preventivi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Numero di telefono:</strong> utilizzato per contattarti riguardo la tua richiesta</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Città:</strong> per valutare la copertura del servizio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Messaggio:</strong> eventuali note o richieste specifiche</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Dati Raccolti Automaticamente</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Indirizzo IP:</strong> raccolto automaticamente per motivi di sicurezza e statistiche</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Tipo di browser e dispositivo:</strong> per ottimizzare l'esperienza di navigazione</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Pagine visitate:</strong> per comprendere l'utilizzo del sito</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span><strong>Data e ora di accesso:</strong> per analisi statistiche</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Finalità del Trattamento */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <FileText className="text-green-600" size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Finalità del Trattamento</h2>
              <p className="text-gray-700 mb-6">
                I dati personali raccolti vengono trattati per le seguenti finalità:
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">1. Gestione delle Richieste</h4>
                  <p className="text-gray-700">
                    Elaborazione delle richieste di preventivo e fornitura di informazioni sui nostri servizi.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">2. Comunicazioni Commerciali</h4>
                  <p className="text-gray-700">
                    Invio di comunicazioni relative ai nostri servizi, promozioni e novità (solo previo consenso).
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">3. Obblighi Legali</h4>
                  <p className="text-gray-700">
                    Adempimento di obblighi previsti da leggi, regolamenti e normativa comunitaria.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">4. Sicurezza e Analisi</h4>
                  <p className="text-gray-700">
                    Prevenzione di frodi, miglioramento del sito e analisi statistiche aggregate.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong className="text-slate-900">Base giuridica:</strong> Il trattamento è basato sul consenso 
                  dell'interessato, sull'esecuzione di un contratto o su un legittimo interesse del titolare.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Diritti dell'Utente */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <Shield className="text-green-600" size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Diritti dell'Utente</h2>
              <p className="text-gray-700 mb-6">
                In conformità al GDPR, hai diritto di:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Accesso</h4>
                  <p className="text-gray-700 text-sm">Ottenere informazioni sui tuoi dati trattati</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Rettifica</h4>
                  <p className="text-gray-700 text-sm">Correggere dati inesatti o incompleti</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Cancellazione</h4>
                  <p className="text-gray-700 text-sm">Richiedere la rimozione dei tuoi dati</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Limitazione</h4>
                  <p className="text-gray-700 text-sm">Limitare il trattamento dei dati</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Portabilità</h4>
                  <p className="text-gray-700 text-sm">Ricevere i dati in formato strutturato</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">✓ Opposizione</h4>
                  <p className="text-gray-700 text-sm">Opporti al trattamento per motivi legittimi</p>
                </div>
              </div>

              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong className="text-slate-900">Come esercitare i tuoi diritti:</strong><br />
                  Invia una richiesta via email a <a href={`mailto:${companyInfo.email}`} className="text-green-600 hover:text-green-700 font-semibold">{companyInfo.email}</a> o 
                  contattaci telefonicamente al {companyInfo.phone}.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cookie Policy */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <Cookie className="text-green-600" size={28} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Cookie Policy</h2>
              <p className="text-gray-700 mb-6">
                Il nostro sito utilizza cookie per migliorare l'esperienza di navigazione e fornire funzionalità avanzate.
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Cosa sono i Cookie?</h3>
                  <p className="text-gray-700">
                    I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. 
                    Permettono al sito di ricordare le tue azioni e preferenze.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Tipologie di Cookie Utilizzati</h3>
                  
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Cookie Tecnici (Necessari)</h4>
                      <p className="text-gray-700 text-sm mb-2">
                        Essenziali per il funzionamento del sito. Non richiedono consenso.
                      </p>
                      <ul className="text-gray-600 text-sm space-y-1">
                        <li>• Gestione della sessione</li>
                        <li>• Funzionalità di sicurezza</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Cookie Analitici</h4>
                      <p className="text-gray-700 text-sm mb-2">
                        Ci aiutano a capire come i visitatori utilizzano il sito.
                      </p>
                      <ul className="text-gray-600 text-sm space-y-1">
                        <li>• Google Analytics (anonimizzato)</li>
                        <li>• Statistiche di utilizzo</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Cookie di Terze Parti</h4>
                      <p className="text-gray-700 text-sm mb-2">
                        Cookie gestiti da servizi esterni integrati nel sito.
                      </p>
                      <ul className="text-gray-600 text-sm space-y-1">
                        <li>• Google Maps (per visualizzare la mappa)</li>
                        <li>• Social media plugins</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">Come Gestire i Cookie</h3>
                  <p className="text-gray-700 mb-3">
                    Puoi controllare e/o eliminare i cookie come desideri. Puoi eliminare tutti i cookie già presenti 
                    sul tuo computer e impostare quasi tutti i browser in modo da bloccarne l'installazione.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-gray-700 text-sm">
                      <strong>Nota:</strong> Bloccando tutti i cookie, alcune funzionalità del sito potrebbero non funzionare correttamente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conservazione e Sicurezza */}
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Conservazione e Sicurezza dei Dati</h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong className="text-slate-900">Periodo di conservazione:</strong> I dati personali vengono conservati 
              per il tempo necessario a soddisfare le finalità per cui sono stati raccolti, nel rispetto degli obblighi di legge.
            </p>
            <p>
              <strong className="text-slate-900">Misure di sicurezza:</strong> Adottiamo misure tecniche e organizzative 
              appropriate per proteggere i dati personali contro la distruzione, la perdita, la modifica, 
              la divulgazione o l'accesso non autorizzati.
            </p>
          </div>
        </div>

        {/* Modifiche */}
        <div className="bg-gradient-to-r from-green-50 to-gray-50 rounded-2xl p-8 border border-green-100">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Modifiche alla Privacy Policy</h2>
          <p className="text-gray-700">
            Ci riserviamo il diritto di modificare questa Privacy Policy in qualsiasi momento. 
            Le modifiche entreranno in vigore immediatamente dopo la loro pubblicazione sul sito. 
            Ti invitiamo a consultare periodicamente questa pagina per rimanere aggiornato.
          </p>
        </div>

        {/* Contatti */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Per qualsiasi domanda o richiesta relativa alla presente Privacy Policy:
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`mailto:${companyInfo.email}`}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all font-semibold"
            >
              Contattaci via Email
            </a>
            <a 
              href={`tel:${companyInfo.phone}`}
              className="bg-slate-900 text-white px-6 py-3 rounded-lg hover:bg-slate-800 transition-all font-semibold"
            >
              Chiamaci: {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
